import { Component, OnInit, ViewChild } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { LocalStorageService } from "../shared/local-storage.service";
import { Helicopter } from "../shared/models/helicopter.model";
import { NextScheduledInspectionsDialogComponent } from "../htl/next-scheduled-inspections-dialog/next-scheduled-inspections-dialog.component";
import { HelicoptersService } from "./helicopters.service";
import { MatTable } from "@angular/material/table";
import moment from "moment";
import { TranslateService } from "@ngx-translate/core";
import { environment } from "projects/htl-part/src/environments/environment";
import { HelicoptersUploadDocumentDialogComponent } from "./helicopters-upload-document-dialog/helicopters-upload-document-dialog.component";
import { HelicoptersDeleteDocumentDialogComponent } from "./helicopters-delete-document-dialog/helicopters-delete-document-dialog.component";
import { HtlBlock } from "../shared/models/htlBlock.model";
import { HHMM } from "@rrsolutionsit/atl-utils";

@Component({
    selector: "app-helicopters",
    templateUrl: "./helicopters.component.html",
    styleUrls: ["./helicopters.component.scss"],
    standalone: false,
})
export class HelicoptersComponent implements OnInit {
    @ViewChild("helicopterTable", { static: false }) helicopterTable: MatTable<any>;
    @ViewChild("fileInputPDF", { static: false }) fileInputPDF: any;

    displayedColumns: string[] = [
        "position",
        "operator",
        "registrationMark",
        "serialNumber",
        "type",
        "airframeHours",
        "engine1Hours",
        "engine2Hours",
        "firstCalendarType",
        "firstCalendarNextDue",
        "firstCalendarRemaining",
        "firstLimitHourlyType",
        "firstLimitHourlyNextDue",
        "firstLimitHourlyRemaining",
        "status",
        "document",
        "actions",
    ];
    dataSource: Helicopter[] = [];
    expiredArray: boolean[] = [];
    helicopterAttr: any;

    private _userSession;

    public environment = environment;
    public fileToUpload: File | null = null;

    public isCamo = false;

    constructor(
        private _helicoptersService: HelicoptersService,
        private _localStorageService: LocalStorageService,
        private _dialog: MatDialog,
        private _translate: TranslateService,
    ) {
        this._userSession = this._localStorageService.getUserLocalStorageObject();
    }

    ngOnInit(): void {
        if (this._userSession.htlAccountTypes.length > 0) {
            switch (this._userSession.htlAccountTypes[0].id) {
                case 1:
                    this.isCamo = true;
                    break;
                case 2:
                    this.isCamo = true;
                    break;
            }
        }
        this._helicoptersService.getHelicopterAttributes(this._userSession.organizationId).subscribe((attrs) => {
            this.helicopterAttr = attrs;
            this._helicoptersService.getHelicopters(this._userSession.organizationId).subscribe((result) => {
                this.dataSource = result.filter((h) => h.status === 1).concat(result.filter((h) => h.status === 3));
                this.expiredArray = [];
                this.dataSource.forEach((h) => {
                    const dueDate = new Date(h.firstCalendarNextDue);
                    const today = new Date();
                    if (h.firstCalendarNextDue) {
                        h.firstCalendarRemaining = Math.floor((dueDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)) + 1;
                    }
                    if (h.firstLimitHourlyNextDue) {
                        h.firstLimitHourlyRemaining = HHMM.subtractTimes(h.firstLimitHourlyNextDue, h.airFrameTotalHours).value;
                    }
                    if (parseInt(h.firstLimitHourlyNextDue) < 0 || dueDate < today || parseInt(h.firstLimitHourlyNextDue) < parseInt(h.airFrameTotalHours)) {
                        this.expiredArray.push(true);
                    } else {
                        this.expiredArray.push(false);
                    }
                });
            });
        });
    }

    getHelicopterOperator(operatorId: number): string {
        const operators = this.helicopterAttr.operators;
        const filteredOp = operators.filter((o) => o.id === operatorId)[0];
        return filteredOp ? filteredOp.name : "N/D";
    }

    getHelicopterType(helicopterTypeId: number): string {
        const helicopterTypes = this.helicopterAttr.helicopterTypes;
        const filteredType = helicopterTypes.filter((t) => t.id === helicopterTypeId)[0];
        return filteredType ? filteredType.name : "N/D";
    }

    openNextScheduleInspectionDialog(helicopterId: number) {
        const dialogRef = this._dialog.open(NextScheduledInspectionsDialogComponent, {
            disableClose: true,
            width: "50%",
            data: {
                helicopterId: helicopterId,
            },
        });

        dialogRef.afterClosed().subscribe((result) => {
            const helicopterObject = this.dataSource.filter((d) => d.id === result.helicopterId)[0];
            if (helicopterObject) {
                helicopterObject.firstCalendarNextDue = result.firstCalendarNextDue;
                helicopterObject.firstCalendarType = result.firstCalendarType;
                helicopterObject.firstLimitHourlyNextDue = result.firstLimitHourlyNextDue;
                helicopterObject.firstLimitHourlyType = result.firstLimitHourlyType;
                this.helicopterTable.renderRows();
            }
        });
    }

    public getStatus(status: number): string {
        switch (status) {
            case 1:
                return this._translate.instant("HELICOPTERS.active");
            case 2:
                return this._translate.instant("HELICOPTERS.inactive");
            case 3:
                return this._translate.instant("HELICOPTERS.maintenance");
            default:
                return "";
        }
    }

    handlePDFFileInput(eventTarget: any, helicopterId: number) {
        const file = eventTarget.files[0];
        if (!file) {
            return;
        }

        this.fileToUpload = file;

        this.openUploadDocumentDialog(helicopterId);
    }

    openUploadDocumentDialog(helicopterId: number) {
        const dialogRef = this._dialog.open(HelicoptersUploadDocumentDialogComponent, {
            width: "20%",
            data: { fileName: this.fileToUpload.name },
        });
        dialogRef.afterClosed().subscribe((result) => {
            if (result) {
                this._helicoptersService.uploadHelicopterDocument(this.fileToUpload, helicopterId).subscribe((res) => {
                    this.ngOnInit();
                });
            } else {
                this.fileToUpload = null;
                this.fileInputPDF.nativeElement.value = "";
            }
        });
    }

    openDeleteDialog(helicopter) {
        const dialogRef = this._dialog.open(HelicoptersDeleteDocumentDialogComponent, {
            width: "20%",
            data: { fileName: helicopter.docs[0].filename },
        });
        dialogRef.afterClosed().subscribe((result) => {
            if (result) {
                this._helicoptersService.deleteHelicopterDocument(helicopter.docs[0].id, helicopter.id, helicopter.docs[0].filename).subscribe((res) => {
                    this.ngOnInit();
                });
            }
        });
    }

    getStyles(index) {
        const styles: { [key: string]: string } = {};
        if (this.expiredArray[index]) {
            styles["color"] = "red";
        }

        if (this.dataSource[index].status === 3) {
            styles["background"] = "yellow";
        }

        return styles;
    }
}
